import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Stack,
  Popover,
  FormControl,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  TextField,
  MenuItem,
} from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../axios";
import { Rating } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

const headerStyle = {
  fontFamily: "Roboto",
  fontSize: "16px",
  fontWeight: "500",
  color: "var(--pg-text)",
  backgroundColor: "var(--pg-surface-muted)",
  textAlign: "left",
};

const cellStyle = {
  fontFamily: "Roboto",
  fontSize: "14px",
  fontWeight: "400",
  color: "var(--pg-success)",
  px: "14px",
  py: "8px",
};

const cellStyles = {
  fontFamily: "Roboto",
  fontSize: "14px",
  fontWeight: "400",
  color: "var(--pg-text)",
  px: "14px",
  py: "8px",
};

function ViewClicks() {
  const [clicksData, setClicksData] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("All");
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [anchorElDateRange, setAnchorElDateRange] = useState(null);
  const [isDateRangeInvalid, setIsDateRangeInvalid] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const navigate = useNavigate();
  const { businessId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {t}= useTranslation();

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        if (!businessId) {
          throw new Error("Authentication required");
        }

        const response = await axiosInstance.post(
          "places.php?method=business_providers",
          { business_id: businessId }
        );

        if (response.data.status === "success" && response.data.providers) {
          setProviders(response.data.providers);
        } else {
          console.error("Error: Missing providers in API response");
        }
      } catch (error) {
        console.error("API Fetch Error:", error);
      }
    };

    fetchProviders();
  }, [businessId]);

  const fetchClicksCount = async (keyword = "") => {
    try {
      const payload = { business_id: businessId };

      if (keyword && keyword.length >= 4) {
        payload.keyword = keyword;
      }

      if (selectedDateRange) {
        payload.start_date = format(selectedDateRange.startDate, "yyyy-MM-dd");
        payload.end_date = format(selectedDateRange.endDate, "yyyy-MM-dd");
      }

      const response = await axiosInstance.post(
        "orders.php?method=get_business_clicks_count",
        payload
      );

      const totalclicks = parseInt(response.data.clicks_count[0].totalclicks);
      const pages = Math.ceil(totalclicks / 10);
      setTotalPages(pages);
    } catch (err) {
      console.log("Failed to fetch orders count");
    }
  };

  const fetchClicks = async (
    page = 1,
    showPaginationLoader = false,
    keyword = ""
  ) => {
    if (!businessId) {
      console.error("Business ID missing!", businessId);
      setLoading(false);
      setClicksData([]);
      return;
    }

    if (showPaginationLoader) {
      setPaginationLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const payload = { page, business_id: businessId };

      if (keyword && keyword.length >= 4) {
        payload.keyword = keyword;
      }

      if (selectedDateRange) {
        payload.start_date = format(selectedDateRange.startDate, "yyyy-MM-dd");
        payload.end_date = format(selectedDateRange.endDate, "yyyy-MM-dd");
      }

      const response = await axiosInstance.post(
        "orders.php?method=get_business_clicks",
        payload
      );

      if (response.data?.clicks) {
        setClicksData(response.data.clicks);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Error fetching clicks:", error);
      setClicksData([]);
    } finally {
      if (showPaginationLoader) {
        setPaginationLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const keyword = selectedProvider !== "All" ? selectedProvider : "";
    const bothDatesSelected = selectedDateRange !== null;
    const noDatesSelected = selectedDateRange === null;

    if (bothDatesSelected || noDatesSelected) {
      fetchClicks(1, false, keyword);
      fetchClicksCount(keyword);
    }
  }, [selectedProvider, selectedDateRange, businessId]);

  const handlePageChange = (event, value) => {
    if (isSearching && searchTerm.length >= 4) {
      fetchClicks(value, true, searchTerm);
    } else {
      fetchClicks(value, true);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const handleNavigateBack = () => {
    navigate("/dashboard/businessreports");
  };

  const handleOpenDateRange = (event) => {
    setAnchorElDateRange(event.currentTarget);
  };

  const handleApplyDateRange = () => {
    const { startDate, endDate } = dateRange[0];
    if (!endDate || startDate.getTime() === endDate.getTime()) {
      setIsDateRangeInvalid(true);
      return;
    }
    setIsDateRangeInvalid(false);
    setSelectedDateRange(dateRange[0]);
    fetchClicks(1);
    fetchClicksCount();
    setAnchorElDateRange(null);
  };

  const handleResetDateRange = () => {
    setSelectedDateRange(null);
    setSelectedProvider("All");
    fetchClicks(1);
    fetchClicksCount();
    setAnchorElDateRange(null);
  };

  return (
    <Box
      paddingLeft={{ xs: "18px", sm: "30px" }}
      pr={{ xs: "18px", sm: "0px" }}
    >
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="90vh"
          p={3}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: { xs: "block", sm: "none" }, textAlign: "end" }}>
            <IconButton
              onClick={handleNavigateBack}
              sx={{ color: "primary.main" }}
            >
              <ChevronLeft sx={{ color: "primary.main" }} />
              <Typography sx={{ fontWeight: "bold", color: "primary.main" }}>
                {t("Back")}
              </Typography>
            </IconButton>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              ps: { xs: "10px", sm: "0px" },
              pr: { xs: "0px", sm: "8px" },
            }}
          >
            <Box sx={{ pb: "10px", pt: { xs: "10px" } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: { xs: "18px", sm: "28px" },
                    fontWeight: "600",
                    lineHeight: {xs:"24px",sm:"16px"},
                    color: "var(--pg-text)",
                    pr: "4px",
                  }}
                >
                  {t("Business Reports")}
                </Typography>
                {clicksData.length > 0 && (
                  <>
                    <Typography
                     sx={{ color: "var(--pg-text)", fontFamily: "Roboto", lineHeight: {xs:"23px",sm:"20px"}, fontSize: {xs:"18px",sm:"20px"},pr:"8px", fontWeight: 600 ,pl:"4px"}}
                    >
                      -
                    </Typography>
                    <Typography sx={{ color: "var(--pg-text)", fontFamily: "Roboto", lineHeight: {xs:"23px",sm:"20px"}, fontSize: {xs:"16px",sm:"20px"}, fontWeight: 600 ,}}>
                    {clicksData[0].name}
                    </Typography>
                  </>
                )}
              </Box>
              {clicksData.length > 0 && (
                <>
                  <Typography
                    sx={{
                      color: "var(--pg-text-muted)",
                      fontFamily: "Roboto",
                      fontSize: "16px",
                      fontWeight: 400,
                      py: "9px",
                    }}
                  >
                    {clicksData[0].address}
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <IconButton
                onClick={handleNavigateBack}
                sx={{ color: "primary.main" }}
              >
                <ChevronLeft sx={{ color: "primary.main" }} />
                <Typography sx={{ fontWeight: "bold", color: "primary.main" }}>
                 {t("Back")}
                </Typography>
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ pt: "18px", pb: "8px" }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              gap={{ xs: 1, sm: 5 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontWeight: 500,
                    fontSize: "18px",
                    fontFamily: "Roboto",
                  }}
                >
                  {t("Filter Reports by")}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelectedProvider("All");
                    setSelectedDateRange(null);
                    fetchClicks(1);
                    fetchClicksCount();
                  }}
                  sx={{ display: { xs: "block", sm: "none" } }}
                >
                 {t("Reset")}
                </Button>
              </Box>
              <TextField
                select
                label="Social Networks"
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                size="small"
                sx={{
                  minWidth: { xs: "100%", sm: 200 },
                  ml: { xs: "0px !important", sm: "16px !important" },
                  mb: { xs: "6px !important", sm: "0px !important" },
                }}
                InputLabelProps={{
                  sx: {
                    fontSize: "16px",
                    fontWeight: "500",
                    fontFamily: "Roboto",
                    color: "var(--pg-text-muted)",
                    letterSpacing: "0.6px",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {Object.entries(providers).map(([key, val]) => (
                  <MenuItem key={key} value={key}>
                    {val.proivder_label}
                  </MenuItem>
                ))}
                <MenuItem value="review">Review</MenuItem>
                <MenuItem value="scan">Scan</MenuItem>
              </TextField>

              <FormControl
                variant="outlined"
                size="small"
                sx={{
                  minWidth: { xs: "100%", sm: 200 },
                  ml: { xs: "0px !important", sm: "16px !important" },
                  mb: { xs: "6px !important", sm: "0px !important" },
                }}
              >
                <Box
                  onClick={handleOpenDateRange}
                  sx={{
                    border: "1px solid var(--pg-border)",
                    borderRadius: "4px",
                    padding: "8.5px 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: "var(--pg-text)",
                  }}
                >
                  <Typography sx={{ fontFamily: "Roboto" }}>
                    {selectedDateRange
                      ? `${format(
                          selectedDateRange.startDate,
                          "MMM dd"
                        )} - ${format(selectedDateRange.endDate, "MMM dd")}`
                      : "Select Date Range"}
                  </Typography>
                  <CalendarMonthIcon sx={{ ml: 1, color: "grey.600" }} />
                </Box>
              </FormControl>

              <Button
                onClick={handleResetDateRange}
                variant="text"
                size="small"
                sx={{display:{xs:"none",sm:"block"}}}
              >
                {t("Reset")}
              </Button>
            </Stack>
          </Box>

          <TableContainer
            component={Paper}
            sx={{ maxWidth: { xs: "max-content", sm: "80%" }, mt: 3 }}
          >
            <Table sx={{ width: { xs: "max-content", sm: "100%" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerStyle}>{t("Business Name")}</TableCell>
                  <TableCell sx={headerStyle}>{t("Created Date")}</TableCell>
                  <TableCell sx={headerStyle}>{t("Networks")}</TableCell>
                  <TableCell sx={headerStyle}>{t("Clicks")}</TableCell>
                  {/* <TableCell sx={headerStyle}></TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {clicksData.length > 0 ? (
                  clicksData.map((click, index) => (
                    <TableRow key={index}>
                      <TableCell sx={cellStyles}>
                        {" "}
                        <div>
                          <div>
                            <Typography
                              sx={{
                                color: "var(--pg-text)",
                                fontFamily: "Roboto",
                                lineHeight: "20px",
                              }}
                            >
                              {click.name}
                            </Typography>
                          </div>
                          <div style={{ fontSize: "0.90em", color: "var(--pg-text-muted)" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography
                                sx={{
                                  fontFamily: "Roboto",
                                  fontSize: "14px",
                                  px: "3px",
                                  fontWeight: "500",
                                }}
                              >
                                {click.rating}{" "}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Roboto",
                                  fontSize: "14px",
                                  pr: "2px",
                                  fontWeight: "500",
                                }}
                              >
                                ({click?.review_count} reviews)
                              </Typography>
                              <Rating
                                value={parseFloat(click.rating)}
                                readOnly
                                precision={0.1}
                                sx={{ fontSize: "14px" }}
                              />
                            </Box>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell sx={cellStyles}>
                        {formatDate(click.created_at)}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {providers[click.provider]?.proivder_label ||
                          click.provider}
                      </TableCell>
                      <TableCell sx={cellStyle}>{click.click}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{ textAlign: "center", py: 2, fontWeight: 600 }}
                    >
                      {t("No Data Available")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {paginationLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: "rgba(138, 136, 136, 0.6)", // blurred effect
                backdropFilter: "blur(1px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "4px",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box display="flex" justifyContent="center" p={2}>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size={isMobile ? "small" : "large"}
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "50%",
                    width: isMobile ? 30 : 40,
                    height: isMobile ? 30 : 40,
                    minWidth: isMobile ? 30 : 40,
                  },
                }}
              />
            )}
          </Box>
          <Popover
            open={Boolean(anchorElDateRange)}
            anchorEl={anchorElDateRange}
            onClose={() => setAnchorElDateRange(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box p={2}>
              <DateRange
                editableDateInputs={true}
                onChange={(item) => setDateRange([item.selection])}
                moveRangeOnFirstSelection={false}
                ranges={dateRange}
                maxDate={new Date()}
                locale={enUS}
                className={isDateRangeInvalid ? "invalid-date-range" : ""}
              />
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                mt={1}
              >
                <Button
                  size="small"
                  onClick={handleApplyDateRange}
                  variant="contained"
                >
                  {t("Apply")}
                </Button>
              </Stack>
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
}

export default ViewClicks;
